/**
* API Key Validator and Visitor ID Manager
* 
* Handles:
* - API key validation against widgetApiKeys collection
* - Auto-generation and persistence of visitor IDs
* - Domain validation (if configured)
*/

import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore'
import { v4 as uuidv4 } from 'uuid'
import { getFirestoreForProjectKey, getFunctionsBaseUrlByKey, type FirebaseProjectKey } from '../firebase'
import type { IChatWidgetApiKey, IWidgetConfig, IResolvedConfig } from '../types'

const VISITOR_ID_KEY = 'chat_widget_visitor_id'

type WidgetPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'

/** Normalise legacy 'right'/'left' values and missing field to canonical corner strings */
function normalizePosition(raw?: string): WidgetPosition {
    if (raw === 'right' || raw === 'bottom-right' || !raw) return 'bottom-right'
    if (raw === 'left' || raw === 'bottom-left') return 'bottom-left'
    if (raw === 'top-right') return 'top-right'
    if (raw === 'top-left') return 'top-left'
    return 'bottom-right'
}
const VISITOR_NAME_KEY = 'chat_widget_visitor_name'

/**
* Extract Firebase project key from API key format
* 
* API key format: cw_{projectKey}_{uuidWithoutHyphens}
* Examples:
*   - cw_1_550e8400e29b41d4a716446655440000 → "1" (staging)
*   - cw_2_6ba7b8109dad11d180b400c04fd430c8 → "2" (DJC live)
*   - cw_3_7c9e667995b4c2e1a0e1b0c0d0e0f0a0 → "3" (IQPilot)
* 
* @param apiKey - The API key string
* @returns Project key ("1", "2", or "3") or null if invalid format
*/
export function extractProjectKeyFromApiKey(apiKey: string): FirebaseProjectKey | null {
    if (!apiKey || typeof apiKey !== 'string') {
        return null
    }
    
    // API key format: cw_{projectKey}_{...}
    const parts = apiKey.split('_')
    
    // Must have at least 3 parts: "cw", projectKey, and at least one more part
    if (parts.length < 3 || parts[0] !== 'cw') {
        return null
    }
    
    const projectKey = parts[1]
    
    // Validate project key is "1", "2", or "3"
    if (projectKey === '1' || projectKey === '2' || projectKey === '3') {
        return projectKey as FirebaseProjectKey
    }
    
    return null
}

/**
* Generate a unique visitor ID using UUID v4 (shortened format)
* Format: cwv_{shortUuid} (20 characters total: 4 prefix + 16 uuid chars)
* Example: cwv_550e8400e29b41d4
*/
export function generateVisitorId(): string {
    // Generate UUID v4 and remove hyphens
    const uuid = uuidv4().replace(/-/g, '')
    // Take first 16 characters for shorter ID while maintaining uniqueness
    const shortUuid = uuid.substring(0, 16)
    return `cwv_${shortUuid}`
}

/**
* Generate a 4-digit display number from visitor ID
* This creates a pseudo-sequential number that looks nice but doesn't
* require database coordination. Uses hash function for consistency.
* Same visitor ID always produces the same display number.
*/
export function generateDisplayNumberFromId(visitorId: string): string {
    let hash = 0
    for (let i = 0; i < visitorId.length; i++) {
        hash = ((hash << 5) - hash) + visitorId.charCodeAt(i)
        hash = hash & hash // Convert to 32-bit integer
    }
    const displayNum = Math.abs(hash) % 10000
    return displayNum.toString().padStart(4, '0')
}

/**
* Get or create a persistent visitor ID using localStorage
*/
export function getOrCreateVisitorId(providedId?: string): string {
    // If a visitor ID is explicitly provided, use it
    if (providedId) {
        return providedId
    }
    
    // Try to get from localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
        const storedId = localStorage.getItem(VISITOR_ID_KEY)
        if (storedId) {
            return storedId
        }
        
        // Generate and store a new visitor ID
        const newId = generateVisitorId()
        try {
            localStorage.setItem(VISITOR_ID_KEY, newId)
        } catch {
            console.warn('[Chat Widget] Could not save visitor ID to localStorage')
        }
        return newId
    }
    
    // Fallback: generate a session-only ID
    return generateVisitorId()
}

/**
* Get or set visitor display name
* Generates "Visitor #XXXX" format if not provided
* @param providedName - Optional name provided by client
* @param visitorId - Visitor ID used to generate display number
*/
export function getVisitorDisplayName(providedName?: string, visitorId?: string): string {
    if (providedName) {
        // Save the provided name
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                localStorage.setItem(VISITOR_NAME_KEY, providedName)
            } catch {
                // Ignore storage errors
            }
        }
        return providedName
    }
    
    // Try to get from localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
        const storedName = localStorage.getItem(VISITOR_NAME_KEY)
        if (storedName) {
            return storedName
        }
    }
    
    // Generate display name with number from visitor ID
    if (visitorId) {
        const displayNum = generateDisplayNumberFromId(visitorId)
        const generatedName = `Visitor #${displayNum}`
        
        // Store it for consistency
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                localStorage.setItem(VISITOR_NAME_KEY, generatedName)
            } catch {
                // Ignore storage errors
            }
        }
        
        return generatedName
    }
    
    // Fallback
    return 'Visitor'
}

/**
* Get or set visitor name (DEPRECATED - use getVisitorDisplayName instead)
* Kept for backward compatibility
*/
export function getVisitorName(providedName?: string): string {
    // Get visitorId from localStorage for generating display number
    const visitorId = typeof window !== 'undefined' && window.localStorage
        ? localStorage.getItem(VISITOR_ID_KEY)
        : undefined
    
    return getVisitorDisplayName(providedName, visitorId || undefined)
}

/**
* Validate API key against Firestore
* 
* New approach: Extract project key from API key format (cw_{projectKey}_{...})
* and connect directly to the client project for validation.
*/
export async function validateApiKey(apiKey: string): Promise<{
    isValid: boolean
    data?: IChatWidgetApiKey
    firebaseProjectKey?: string
    functionsBaseUrl?: string
    error?: string
}> {
    if (!apiKey || typeof apiKey !== 'string' || apiKey.trim() === '') {
        return {
            isValid: false,
            error: 'API key is required',
        }
    }
    
    // Step 1: Extract project key from API key format
    const firebaseProjectKey = extractProjectKeyFromApiKey(apiKey)
    
    if (!firebaseProjectKey) {
        return {
            isValid: false,
            error: 'Invalid API key format. Expected format: cw_{projectKey}_{...}',
        }
    }
    
    // Step 2: Connect directly to client project using extracted project key
    const db = getFirestoreForProjectKey(firebaseProjectKey)
    
    if (!db) {
        return {
            isValid: false,
            error: `Failed to connect to Firebase project for key: ${firebaseProjectKey}`,
        }
    }
    
    try {
        // Step 3: Query widgetApiKeys collection in CLIENT project
        const apiKeyQuery = query(
            collection(db, 'widgetApiKeys'),
            where('apiKey', '==', apiKey)
        )
        
        const querySnapshot = await getDocs(apiKeyQuery)
        
        if (querySnapshot.empty) {
            return {
                isValid: false,
                error: 'Invalid API key',
            }
        }
        
        const apiKeyDoc = querySnapshot.docs[0]
        const apiKeyData = { id: apiKeyDoc.id, ...apiKeyDoc.data() } as IChatWidgetApiKey
        
        // Step 4: Verify project key matches (security check)
        if (apiKeyData.firebaseProjectKey && apiKeyData.firebaseProjectKey !== firebaseProjectKey) {
            return {
                isValid: false,
                error: 'API key project key mismatch. The API key format does not match the stored project key.',
            }
        }
        
        // Step 5: Check status
        if (apiKeyData.status !== 'active') {
            return {
                isValid: false,
                error: `API key is ${apiKeyData.status}`,
            }
        }
        
        // Step 6: Automatically derive functions URL from project key
        const functionsBaseUrl = getFunctionsBaseUrlByKey(firebaseProjectKey)
        
        // Step 7: Optional: Validate domain
        if (apiKeyData.allowedDomains && apiKeyData.allowedDomains.length > 0) {
            const currentDomain = typeof window !== 'undefined' ? window.location.hostname : ''
            const normalizedDomain = currentDomain.toLowerCase().replace(/^www\./, '')
            
            const isAllowed = apiKeyData.allowedDomains.some(allowedDomain => {
                const normalized = allowedDomain.toLowerCase().replace(/^www\./, '')
                return normalizedDomain === normalized || normalizedDomain.endsWith(`.${normalized}`)
            })
            
            if (!isAllowed) {
                return {
                    isValid: false,
                    error: 'Domain not allowed for this API key',
                }
            }
        }
        
        // Step 8: Update lastUsedAt (fire and forget)
        updateDoc(doc(db, 'widgetApiKeys', apiKeyDoc.id), {
            lastUsedAt: new Date(),
        }).catch(err => {
            console.warn('[Chat Widget] Could not update lastUsedAt:', err)
        })
        
        return {
            isValid: true,
            data: apiKeyData,
            firebaseProjectKey,
            functionsBaseUrl,
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Validation failed'
        console.error('[Chat Widget] API key validation error:', error)
        return {
            isValid: false,
            error: errorMessage,
        }
    }
}

/**
* Resolve full configuration from API key and user options
* Customization settings are only loaded from the database (apiKeyData), not from config
*/
export async function resolveConfig(config: IWidgetConfig): Promise<IResolvedConfig> {
    const DEFAULT_PRIMARY = '#6366f1'
    const DEFAULT_GREETING = 'Hi there! How can I help you today?'
    
    // Validate API key
    const validation = await validateApiKey(config.apiKey)
    
    // Get visitor ID first (needed for display name generation)
    const visitorId = getOrCreateVisitorId(config.visitorId)
    
    if (!validation.isValid || !validation.data) {
        return {
            apiKey: config.apiKey,
            clientId: '',
            userId: '',
            visitorId: visitorId,
            visitorName: getVisitorDisplayName(config.visitorName, visitorId),
            theme: 'light',
            primaryColor: DEFAULT_PRIMARY,
            useGradient: true,
            greeting: DEFAULT_GREETING,
            allowStopAI: true,
            allowFileAttachment: true,
            openOnLoad: false,
            floatingEnabled: false,
            position: 'bottom-right',
            firebaseProjectKey: '1', // Default fallback
            functionsBaseUrl: '',
            isValid: false,
            error: validation.error,
        }
    }
    
    const apiKeyData = validation.data
    
    // Extract Firebase project key and functions URL from validation
    const firebaseProjectKey = validation.firebaseProjectKey as '1' | '2' | '3'
    const functionsBaseUrl = validation.functionsBaseUrl || ''
    
    if (!firebaseProjectKey) {
        return {
            apiKey: config.apiKey,
            clientId: '',
            userId: '',
            visitorId: visitorId,
            visitorName: getVisitorDisplayName(config.visitorName, visitorId),
            theme: 'light',
            primaryColor: DEFAULT_PRIMARY,
            useGradient: true,
            greeting: DEFAULT_GREETING,
            allowStopAI: true,
            allowFileAttachment: true,
            openOnLoad: false,
            floatingEnabled: false,
            position: 'bottom-right',
            firebaseProjectKey: '1', // Default fallback
            functionsBaseUrl: '',
            isValid: false,
            error: 'Firebase project key not found',
        }
    }
    
    return {
        apiKey: config.apiKey,
        clientId: apiKeyData.clientId,
        userId: apiKeyData.userId,
        visitorId: visitorId,
        visitorName: getVisitorDisplayName(config.visitorName, visitorId),
        // All customization settings come from database only
        theme: apiKeyData.theme || 'light',
        primaryColor: apiKeyData.primaryColor || DEFAULT_PRIMARY,
        useGradient: apiKeyData.useGradient !== undefined ? apiKeyData.useGradient : true,
        greeting: apiKeyData.greeting || DEFAULT_GREETING,
        allowStopAI: apiKeyData.allowStopAI !== undefined ? apiKeyData.allowStopAI : true,
        allowFileAttachment: apiKeyData.allowFileAttachment !== undefined ? apiKeyData.allowFileAttachment : true,
        openOnLoad: apiKeyData.openOnLoad === true,
        floatingEnabled: config.floatingEnabled ?? apiKeyData.floatingEnabled === true,
        position: normalizePosition(config.position ?? apiKeyData.position),
        logoUrl: apiKeyData.logoUrl || config.logoUrl,
        inactivityResetDuration: apiKeyData.inactivityResetDuration ?? null,
        firebaseProjectKey,
        functionsBaseUrl,
        isValid: true,
    }
}
